import type { ReactNode } from 'react';
import React, { useContext } from 'react';

import { PERMISSION_VERBS } from './PermissionUtils';

/**
 * Why every write on the screen below is refused for now, or null when nothing is. The permission seam
 * (`useRestrictedPermissions`, `PermissionsButtonWrapper`, `PermissionDeniedTooltip`) disables a control
 * needing a write verb while it is set, with the reason as its tooltip, as it does one the user has no
 * permission for. Read verbs and hidden content are left alone.
 */
const WriteFreezeContext = React.createContext<string | null>(null);

interface WriteFreezeProviderProps {
  reason: string | null;
  children: ReactNode;
}

export const WriteFreezeProvider: React.FC<WriteFreezeProviderProps> = ({ reason, children }) => (
  <WriteFreezeContext.Provider value={reason}>{children}</WriteFreezeContext.Provider>
);

const isWriteVerb = (verb: string): boolean => verb === PERMISSION_VERBS.WRITE || verb === PERMISSION_VERBS.FULL;

/** Why a control needing `verb` (write, when none is given) is refused now, or null when it is not. */
export const useWriteFreezeReason = (verb?: string): string | null => {
  const reason = useContext(WriteFreezeContext);
  return isWriteVerb(verb ?? PERMISSION_VERBS.WRITE) ? reason : null;
};
